/**
 * Techosolution Project OS — Multi-Platform Pipeline & Automated Net Profit Engine
 * Comprehensive Channel Financials, Order Importer, TOS Compliance Guard & Message Generator
 */

export interface PlatformFeeConfig {
  platform: 'fiverr' | 'upwork' | 'direct' | 'other' | 'internal';
  commissionRate: number; // e.g. 0.20 for 20%
  label: string;
  badgeColor: string;
}

export const PLATFORM_FEE_RATES: Record<string, number> = {
  fiverr: 0.20, // 20% commission
  upwork: 0.10, // 10% commission
  direct: 0.00, // 0% commission (100% agency take-home)
  other: 0.00,
  internal: 0.00,
};

export const DIRECT_PROCESSOR_FEE_RATE = 0.029; // Optional 2.9% payment processor fee for direct payments

/**
 * Normalizes platform name string to standard lowercase key
 */
export function normalizePlatformName(platform?: string | null): 'fiverr' | 'upwork' | 'direct' | 'other' {
  if (!platform) return 'direct';
  const p = platform.trim().toLowerCase();
  if (p === 'fiverr') return 'fiverr';
  if (p === 'upwork') return 'upwork';
  if (p === 'direct') return 'direct';
  return 'other';
}

/**
 * Returns platform commission cut percentage (0.20 for Fiverr, 0.10 for Upwork, 0.00 for Direct)
 */
export function getPlatformFeeRate(platform?: string | null): number {
  const norm = normalizePlatformName(platform);
  return PLATFORM_FEE_RATES[norm] ?? 0.0;
}

export interface PlatformFinancials {
  gross: number;
  commissionRate: number;
  commissionPercent: number;
  platformCut: number;
  processorFee: number;
  netRevenue: number;
  marginPercent: number;
}

/**
 * Computes platform commission cut, net agency take-home, and margin percentage.
 */
export function calculatePlatformCut(
  grossAmount: number,
  platform?: string | null,
  includeProcessorFee = false
): PlatformFinancials {
  const gross = Math.max(0, Number(grossAmount) || 0);
  const rate = getPlatformFeeRate(platform);
  const commissionPercent = Math.round(rate * 100);
  const platformCut = Math.round(gross * rate * 100) / 100;

  let processorFee = 0;
  if (includeProcessorFee && normalizePlatformName(platform) === 'direct') {
    processorFee = Math.round(gross * DIRECT_PROCESSOR_FEE_RATE * 100) / 100;
  }

  const netRevenue = Math.max(0, Math.round((gross - platformCut - processorFee) * 100) / 100);
  const marginPercent = gross > 0 ? Math.round((netRevenue / gross) * 1000) / 10 : 100;

  return {
    gross,
    commissionRate: rate,
    commissionPercent,
    platformCut,
    processorFee,
    netRevenue,
    marginPercent,
  };
}

/**
 * Calculates Effective Hourly Yield ($/hr) based on net take-home revenue and engineering hours recorded.
 */
export function calculateEffectiveHourlyYield(netRevenue: number, recordedHours: number): number {
  const net = Math.max(0, Number(netRevenue) || 0);
  const hours = Math.max(0, Number(recordedHours) || 0);
  if (hours <= 0) return 0;
  return Math.round((net / hours) * 100) / 100;
}

// =========================================================================
// OPTION 2: SMART 1-CLICK MULTI-CHANNEL ORDER IMPORTER
// =========================================================================

export interface ParsedMilestone {
  title: string;
  order: number;
  description?: string;
  due_date?: string | null;
}

export interface ParsedPlatformOrder {
  platform: 'fiverr' | 'upwork' | 'direct';
  order_id: string;
  client_name: string;
  client_username?: string;
  project_name: string;
  gross_amount: number;
  platform_cut: number;
  net_amount: number;
  margin_percent: number;
  deadline?: string | null;
  revisions?: number;
  escrow_status?: 'funded' | 'in_review' | 'released';
  hourly_limit?: number;
  scope_description: string;
  milestones: ParsedMilestone[];
  platform_url?: string;
}

/**
 * Parses raw text from Fiverr order confirmations, Upwork contract offers, or Direct client proposals.
 */
export function parsePlatformOrderText(rawText: string, defaultPlatform?: string): ParsedPlatformOrder {
  const text = (rawText || '').trim();
  const lower = text.toLowerCase();

  // 1. Detect Platform
  let platform: 'fiverr' | 'upwork' | 'direct' = 'direct';
  if (
    lower.includes('fiverr') ||
    /\bfo[0-9a-z]{7,15}\b/i.test(text) ||
    lower.includes('gig:') ||
    lower.includes('buyer:')
  ) {
    platform = 'fiverr';
  } else if (
    lower.includes('upwork') ||
    /\bup-?[0-9a-z]{6,15}\b/i.test(text) ||
    lower.includes('escrow') ||
    lower.includes('hourly limit') ||
    lower.includes('contract id')
  ) {
    platform = 'upwork';
  } else if (defaultPlatform) {
    platform = normalizePlatformName(defaultPlatform) as 'fiverr' | 'upwork' | 'direct';
  }

  // 2. Extract Order / Contract ID
  let order_id = '';
  const foMatch = text.match(/\b(FO[0-9A-Z]{6,14})\b/i);
  const upMatch = text.match(/\b(UP-?[0-9A-Z]{6,14})\b/i);
  const genericIdMatch = text.match(/(?:Order|Contract|ID|#)[\s:#]+([A-Za-z0-9_-]+)/i);

  if (platform === 'fiverr' && foMatch) {
    order_id = foMatch[1].toUpperCase();
  } else if (platform === 'upwork' && upMatch) {
    order_id = upMatch[1].toUpperCase();
  } else if (foMatch) {
    order_id = foMatch[1].toUpperCase();
    platform = 'fiverr';
  } else if (upMatch) {
    order_id = upMatch[1].toUpperCase();
    platform = 'upwork';
  } else if (genericIdMatch) {
    order_id = genericIdMatch[1].trim();
  } else {
    // Generate fallback unique identifier
    order_id = (platform === 'fiverr' ? 'FO' : platform === 'upwork' ? 'UP-' : 'DIR-') +
      Math.random().toString(36).substring(2, 8).toUpperCase();
  }

  // 3. Extract Client Username / Name
  let client_name = '';
  let client_username = '';

  const buyerMatch = text.match(/(?:Buyer|Client|Customer|Purchased by)[\s:]+([^\n\r,]+)/i);
  const usernameMatch = text.match(/@([A-Za-z0-9_.-]{3,30})/);

  if (buyerMatch) {
    const rawClient = buyerMatch[1].trim();
    // Check if format is "Name (username)"
    const parentheticalMatch = rawClient.match(/^(.*?)\s*\((.*?)\)$/);
    if (parentheticalMatch) {
      client_name = parentheticalMatch[1].trim();
      client_username = parentheticalMatch[2].trim().replace(/^@/, '');
    } else {
      client_name = rawClient;
      if (/^[a-z0-9_.-]+$/i.test(rawClient)) {
        client_username = rawClient;
      }
    }
  } else if (usernameMatch) {
    client_username = usernameMatch[1];
    client_name = usernameMatch[1];
  } else {
    client_name = platform === 'fiverr' ? 'Fiverr Client' : platform === 'upwork' ? 'Upwork Client' : 'Direct Client';
  }

  // 4. Extract Title / Project Name
  let project_name = '';
  const gigMatch = text.match(/(?:Gig|Job|Contract|Project|Title)[\s:]+([^\n\r]+)/i);
  if (gigMatch) {
    project_name = gigMatch[1].trim();
  } else {
    project_name = `${client_name} — ${platform.charAt(0).toUpperCase() + platform.slice(1)} Project`;
  }

  // 5. Extract Gross Amount / Budget
  let gross_amount = 0;
  // Support standard and international currency formats ($, €, £, ¥, A$, C$, etc. and European 1.250,00)
  const priceMatches = text.match(/(?:Total Price|Budget|Amount|Price|Gross|Total)[\s:$€£¥A-Z]*([\d,.]+(?:\.\d{1,2}|,\d{2})?)/i);
  if (priceMatches) {
    let rawNum = priceMatches[1].trim();
    // Handle European format: 1.250,00 -> 1250.00
    if (/^\d{1,3}(?:\.\d{3})+(?:,\d{2})$/.test(rawNum)) {
      rawNum = rawNum.replace(/\./g, '').replace(',', '.');
    } else {
      rawNum = rawNum.replace(/,/g, '');
    }
    gross_amount = parseFloat(rawNum) || 0;
  } else {
    // Fallback search for bare currency symbol + number
    const bareMatch = text.match(/[\$€£¥]\s*([\d,.]+(?:\.\d{1,2}|,\d{2})?)/);
    if (bareMatch) {
      let rawNum = bareMatch[1].trim();
      if (/^\d{1,3}(?:\.\d{3})+(?:,\d{2})$/.test(rawNum)) {
        rawNum = rawNum.replace(/\./g, '').replace(',', '.');
      } else {
        rawNum = rawNum.replace(/,/g, '');
      }
      gross_amount = parseFloat(rawNum) || 0;
    }
  }

  // Calculate Net and Platform Cut
  const financials = calculatePlatformCut(gross_amount, platform);

  // 6. Extract Delivery Deadline / Due Date
  let deadline: string | null = null;
  const dateMatch = text.match(/(?:Delivery Date|Deadline|Delivery|Due Date|Due)[\s:]+([A-Za-z0-9,\s-]+?)(?:\r?\n|$)/i);
  if (dateMatch) {
    const rawDate = dateMatch[1].trim();
    const parsed = new Date(rawDate);
    if (!isNaN(parsed.getTime())) {
      deadline = parsed.toISOString().substring(0, 10);
    } else {
      // Try YYYY-MM-DD
      const isoMatch = rawDate.match(/\b\d{4}-\d{2}-\d{2}\b/);
      if (isoMatch) deadline = isoMatch[0];
    }
  }

  // 7. Extract Revisions (Fiverr specific)
  let revisions: number | undefined;
  const revMatch = text.match(/(?:Revisions?|Revision count)[\s:]+(\d+|Unlimited)/i);
  if (revMatch) {
    revisions = revMatch[1].toLowerCase() === 'unlimited' ? 99 : parseInt(revMatch[1], 10);
  }

  // 8. Extract Escrow Status (Upwork specific)
  let escrow_status: 'funded' | 'in_review' | 'released' | undefined;
  if (lower.includes('escrow: funded') || lower.includes('funded milestone') || lower.includes('funded ($')) {
    escrow_status = 'funded';
  } else if (lower.includes('in review') || lower.includes('submitted for approval')) {
    escrow_status = 'in_review';
  } else if (lower.includes('released')) {
    escrow_status = 'released';
  } else if (platform === 'upwork') {
    escrow_status = 'funded';
  }

  // 9. Extract Hourly Limit
  let hourly_limit: number | undefined;
  const hourlyMatch = text.match(/(?:Hourly Limit|Max Hours)[\s:]+(\d+)/i);
  if (hourlyMatch) {
    hourly_limit = parseInt(hourlyMatch[1], 10);
  }

  // 10. Extract Milestones / Scope
  const milestones: ParsedMilestone[] = [];
  const lines = text.split(/\r?\n/);
  let scope_description = '';

  let inMilestoneSection = false;
  let milestoneOrder = 1;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const bulletMatch = trimmed.match(/^(?:(?:\d+[\.\)]|[\-\*•]|\[[\sx]\])\s*)([^\n\r]+)$/i);
    const headerPrefixMatch = trimmed.match(/^(?:(?:Milestone|Phase|Sprint)\s*\d+[\s:\-]+)\s*([^\n\r]+)$/i);
    const isListItem = Boolean(bulletMatch || headerPrefixMatch);

    if (!isListItem && /(?:Milestones?|Scope|Deliverables?|Order Requirements)/i.test(trimmed)) {
      inMilestoneSection = true;
      continue;
    }

    if (inMilestoneSection || isListItem) {
      if (bulletMatch) {
        milestones.push({
          order: milestoneOrder++,
          title: bulletMatch[1].trim(),
          status: 'pending',
        } as any);
      } else if (headerPrefixMatch) {
        milestones.push({
          order: milestoneOrder++,
          title: trimmed,
          status: 'pending',
        } as any);
      } else if (inMilestoneSection && !trimmed.startsWith('Link:') && !trimmed.startsWith('Order #')) {
        scope_description += (scope_description ? '\n' : '') + trimmed;
      }
    }
  }

  // If no structured milestones found, create default milestone
  if (milestones.length === 0) {
    milestones.push({
      order: 1,
      title: 'Milestone 1 — Initial Deliverables & Architecture',
      description: 'Initial sprint deliverables and system foundations',
      due_date: deadline,
    });
  }

  // 11. Platform URL
  let platform_url = '';
  const urlMatch = text.match(/https?:\/\/[^\s]+/);
  if (urlMatch) {
    platform_url = urlMatch[0];
  } else if (platform === 'fiverr' && order_id) {
    platform_url = `https://www.fiverr.com/orders/${order_id}`;
  } else if (platform === 'upwork' && order_id) {
    const cleanId = order_id.replace(/^UP-?/, '');
    platform_url = `https://www.upwork.com/nx/wm/workroom/${cleanId}`;
  }

  return {
    platform,
    order_id,
    client_name,
    client_username,
    project_name,
    gross_amount,
    platform_cut: financials.platformCut,
    net_amount: financials.netRevenue,
    margin_percent: financials.marginPercent,
    deadline,
    revisions,
    escrow_status,
    hourly_limit,
    scope_description,
    milestones,
    platform_url,
  };
}

// =========================================================================
// OPTION 3: TOS COMPLIANCE GUARD & MESSAGE GENERATOR
// =========================================================================

export interface TosViolation {
  type: 'email' | 'phone' | 'whatsapp' | 'off_platform' | 'skype' | 'telegram' | 'discord' | 'direct_payment';
  match: string;
  explanation: string;
}

export interface TosComplianceResult {
  isCompliant: boolean;
  is_compliant?: boolean;
  platform: 'fiverr' | 'upwork' | 'direct' | 'other';
  violations: TosViolation[];
  riskLevel: 'safe' | 'low' | 'high' | 'critical';
  risk_level?: 'safe' | 'low' | 'high' | 'critical';
  warningMessage: string;
  warning_message?: string;
  sanitizedText: string;
  sanitized_text?: string;
}

/**
 * Checks text for platform TOS violations (contact info leaks, off-platform payment solicitation).
 */
export function checkTosCompliance(text: string, platform?: string | null): TosComplianceResult {
  const normPlatform = normalizePlatformName(platform);
  const violations: TosViolation[] = [];
  const content = text || '';

  // Only Fiverr and Upwork have strict off-platform communication / payment bans
  if (normPlatform !== 'fiverr' && normPlatform !== 'upwork') {
    return {
      isCompliant: true,
      is_compliant: true,
      platform: normPlatform,
      violations: [],
      riskLevel: 'safe',
      risk_level: 'safe',
      warningMessage: 'Direct client communication permitted without platform TOS restrictions.',
      warning_message: 'Direct client communication permitted without platform TOS restrictions.',
      sanitizedText: content,
      sanitized_text: content,
    };
  }

  // 1. Check for email addresses (standard and obfuscated, e.g. name [at] domain [dot] com)
  const standardEmailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/gi;
  let match: RegExpExecArray | null;
  while ((match = standardEmailRegex.exec(content)) !== null) {
    violations.push({
      type: 'email',
      match: match[0],
      explanation: `Email address detected: "${match[0]}". Sharing external email addresses violates ${normPlatform === 'fiverr' ? 'Fiverr' : 'Upwork'} Terms of Service and triggers automated account warning flags.`,
    });
  }

  const obfuscatedEmailRegex = /\b[A-Za-z0-9._%+-]+\s*(?:\[at\]|\(at\)|\bat\b)\s*[A-Za-z0-9.-]+\s*(?:\.|\bdot\b|\[dot\]|\(dot\))\s*[A-Za-z]{2,}\b/gi;
  while ((match = obfuscatedEmailRegex.exec(content)) !== null) {
    if (!violations.some((v) => v.match === match![0])) {
      violations.push({
        type: 'email',
        match: match[0],
        explanation: `Obfuscated email address detected: "${match[0]}". Obfuscating contact information to circumvent platform filters violates terms of service.`,
      });
    }
  }

  // 2. Check for Phone numbers (international, domestic, and spaced formats e.g. +92 300 1234567, 0300 123 4567)
  const phoneRegex = /(?:\+?\d{1,4}[-.\s]*)?(?:\(?\d{2,4}\)?[-.\s]*)?\d{3,4}[-.\s]*\d{3,4}\b|\b03\d{2}[-.\s]*\d{3}[-.\s]*\d{4}\b/g;
  while ((match = phoneRegex.exec(content)) !== null) {
    const candidate = match[0].trim();
    // Exclude potential dates (e.g. 2026-11-01), short strings, or simple numbers
    const digitsOnly = candidate.replace(/\D/g, '');
    const isIsoDate = /^\d{4}[-./]\d{2}[-./]\d{2}$/.test(candidate);
    if (!isIsoDate && digitsOnly.length >= 7 && digitsOnly.length <= 15) {
      // Ensure candidate has some phone-like structure or prefix
      if (candidate.startsWith('+') || candidate.startsWith('03') || candidate.includes('-') || candidate.includes('(') || candidate.includes(' ') || digitsOnly.length >= 10) {
        if (!violations.some((v) => v.match === candidate)) {
          violations.push({
            type: 'phone',
            match: candidate,
            explanation: `Phone number detected: "${candidate}". Phone communication outside ${normPlatform === 'fiverr' ? 'Fiverr' : 'Upwork'} is prohibited.`,
          });
        }
      }
    }
  }

  // 3. Check for WhatsApp keywords or links
  const waRegex = /\b(?:whatsapp|wa\.me|chat\.whatsapp\.com)\b/gi;
  while ((match = waRegex.exec(content)) !== null) {
    violations.push({
      type: 'whatsapp',
      match: match[0],
      explanation: `WhatsApp reference detected: "${match[0]}". Mentioning WhatsApp on ${normPlatform === 'fiverr' ? 'Fiverr' : 'Upwork'} will trigger message shadowbans and possible suspension.`,
    });
  }

  // 4. Check for Skype
  const skypeRegex = /\b(?:skype|live:\.cid\.[a-z0-9]+)\b/gi;
  while ((match = skypeRegex.exec(content)) !== null) {
    violations.push({
      type: 'off_platform',
      match: match[0],
      explanation: `Skype reference detected: "${match[0]}". Direct voice/video calls must remain on the approved platform.`,
    });
  }

  // 5. Check for Telegram
  const tgRegex = /\b(?:telegram|t\.me\/[a-z0-9_]+)\b/gi;
  while ((match = tgRegex.exec(content)) !== null) {
    violations.push({
      type: 'off_platform',
      match: match[0],
      explanation: `Telegram reference detected: "${match[0]}". Direct off-platform messaging channels are strictly forbidden.`,
    });
  }

  // 6. Check for Discord
  const discordRegex = /\b(?:discord\.gg|discordapp\.com)\b/gi;
  while ((match = discordRegex.exec(content)) !== null) {
    violations.push({
      type: 'off_platform',
      match: match[0],
      explanation: `Discord link detected: "${match[0]}". External Discord invite links violate terms of service.`,
    });
  }

  // 7. Check for Direct Payment / Fee Avoidance Phrasing & Off-Platform Payment Gateways
  const paymentRegex = /\b(?:pay(?:ment)?(?:\s+\w+){0,3}\s*outside|paypal|pay outside|direct\s*(?:paypal|wire|bank|transfer)|wire\s+transfer|bank\s+transfer|cash\s*app|cashapp|venmo|zelle|usdt|crypto|bitcoin|avoid\s*(?:fee|commission)s?|save\s*(?:fee|commission)s?|circumvent)\b/gi;
  while ((match = paymentRegex.exec(content)) !== null) {
    violations.push({
      type: 'off_platform',
      match: match[0],
      explanation: `Fee circumvention phrasing detected: "${match[0]}". Soliciting direct payments outside ${normPlatform === 'fiverr' ? 'Fiverr' : 'Upwork'} is an immediate zero-tolerance account termination violation.`,
    });
  }

  const isCompliant = violations.length === 0;
  let riskLevel: 'safe' | 'low' | 'high' | 'critical' = 'safe';
  if (violations.some((v) => v.explanation.includes('Fee circumvention') || v.explanation.includes('direct payments'))) {
    riskLevel = 'critical';
  } else if (violations.length >= 2) {
    riskLevel = 'high';
  } else if (violations.length === 1) {
    riskLevel = 'high';
  }

  const warningMessage = isCompliant
    ? 'TOS Compliant: No off-platform contact leaks or fee circumvention detected.'
    : `⚠️ WARNING: ${violations.length} ${normPlatform.toUpperCase()} Terms of Service violation(s) detected. Please remove external contact details before sending or publishing.`;

  // Generate sanitized text with leaks redacted
  let sanitizedText = content;
  for (const v of violations) {
    sanitizedText = sanitizedText.split(v.match).join(`[REDACTED_${v.type.toUpperCase()}]`);
  }

  return {
    isCompliant,
    is_compliant: isCompliant,
    platform: normPlatform,
    violations,
    riskLevel,
    risk_level: riskLevel,
    warningMessage,
    warning_message: warningMessage,
    sanitizedText,
    sanitized_text: sanitizedText,
  };
}

export interface DeliveryMessageContext {
  projectName: string;
  clientName: string;
  orderId?: string | null;
  milestones?: { title: string; status?: string }[];
  portalUrl?: string | null;
  invoiceNumber?: string | null;
  balanceDue?: number | null;
  previewUrl?: string | null;
  revisionsRemaining?: number;
}

/**
 * Generates ready-to-dispatch delivery & follow-up messages tailored to each platform's culture and terms.
 */
export function generateChannelDeliveryMessage(
  channel: 'fiverr' | 'upwork' | 'direct',
  ctx: DeliveryMessageContext
): string {
  const cName = ctx.clientName || 'Valued Client';
  const pName = ctx.projectName || 'Engineering Project';
  const orderRef = ctx.orderId ? ` (Ref: ${ctx.orderId})` : '';

  if (channel === 'fiverr') {
    const milestoneBullets = (ctx.milestones || [])
      .map((m) => `✅ ${m.title}`)
      .join('\n');

    return (
      `Hi ${cName},\n\n` +
      `We are excited to deliver the completed deliverables for **${pName}**${orderRef}!\n\n` +
      `📦 **Completed Sprint Deliverables:**\n` +
      `${milestoneBullets || '✅ Full project implementation verified and production ready.'}\n\n` +
      `🔍 **Review & Testing Instructions:**\n` +
      `Please review the delivery files and verify all agreed requirements. We take great pride in our engineering quality and are dedicated to your 100% satisfaction.\n\n` +
      `🔄 **Revisions & Adjustments:**\n` +
      `If any minor adjustments or tweaks are needed, please click the **"Request Revision"** button and provide your feedback. We will address it immediately.\n\n` +
      `⭐ **Feedback:**\n` +
      `If you are satisfied with the result and our communication, your positive 5-star review means the world to our small engineering team!\n\n` +
      `Thank you for your business and partnership,\n` +
      `TechoSolution Engineering Team`
    );
  }

  if (channel === 'upwork') {
    const milestoneBullets = (ctx.milestones || [])
      .map((m) => `• ${m.title}`)
      .join('\n');

    return (
      `Hello ${cName},\n\n` +
      `We have completed the milestone deliverables for contract **${pName}**${orderRef} and are submitting this milestone for your official review and escrow release.\n\n` +
      `📋 **Completed Scope & Deliverables:**\n` +
      `${milestoneBullets || '• All scheduled milestone tasks completed according to specifications.'}\n\n` +
      (ctx.previewUrl ? `🌐 **Staging / Demonstration Link:**\n${ctx.previewUrl}\n\n` : '') +
      `🤝 **Handover & Next Steps:**\n` +
      `The code, configuration, and documentation have been reviewed and tested. Please inspect the deliverables and click **"Approve & Release Payment"** on the Upwork milestone dashboard.\n\n` +
      `We are ready to proceed with the next milestone immediately upon your confirmation.\n\n` +
      `Best regards,\n` +
      `TechoSolution Agency`
    );
  }

  // Direct Channel (WhatsApp / Email)
  const portalLine = ctx.portalUrl
    ? `\n🔗 *Live Project Portal & Sign-off:* ${ctx.portalUrl}`
    : '';
  const invLine = ctx.invoiceNumber
    ? `\n📄 *Invoice:* #${ctx.invoiceNumber} (Balance Due: $${Number(ctx.balanceDue || 0).toLocaleString()})`
    : '';

  return (
    `Hi ${cName}! 👋\n\n` +
    `Your project *${pName}* has reached an important delivery milestone with TechoSolution.${portalLine}${invLine}\n\n` +
    `You can view live deliverable status, inspect progress evidence, and digitally sign off on milestone completion directly via your secure client portal link.\n\n` +
    `Let us know if you have any questions!\n\n` +
    `Best regards,\n` +
    `*TechoSolution Agency Team*`
  );
}
